/**
 * FlahaSOIL v2 — Scientific Charts composite panel (P1 dark-native polish).
 */
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import type {
  GetSoilTestResponse,
  ScientificAnalysisResponse,
} from "@flaha/shared-types";

import { GlassSurface } from "../../../../layouts/components/GlassSurface";
import { getApiClient } from "../../../../services/apiClientProvider";
import {
  TextureTriangleChart,
  WaterRetentionCurveChart,
  SoilPhysicsWaterChart,
  BaseSaturationChart,
  CationBalanceChart,
  SalinitySodicityChart,
  EvidenceCompletenessChart,
  textureTriangleChartAdapter,
  waterRetentionChartAdapter,
  soilPhysicsChartAdapter,
  baseSaturationChartAdapter,
  cationBalanceChartAdapter,
  salinitySodicityChartAdapter,
  evidenceCompletenessChartAdapter,
} from "../index";

interface ScientificChartsPanelProps {
  soilTestId: string;
  initialAnalysis?: ScientificAnalysisResponse | null;
  initialFullTest?: GetSoilTestResponse | null;
  /**
   * Free plan: only USDA texture triangle (upgrade wall for the rest).
   * Paid plans: full scientific suite.
   */
  textureTriangleOnly?: boolean;
}

export function ScientificChartsPanel({
  soilTestId,
  initialAnalysis,
  initialFullTest,
  textureTriangleOnly = false,
}: ScientificChartsPanelProps) {
  const [analysis, setAnalysis] = useState<ScientificAnalysisResponse | null>(
    initialAnalysis || null
  );
  const [fullTest, setFullTest] = useState<GetSoilTestResponse | null>(
    initialFullTest || null
  );
  const [loading, setLoading] = useState(!initialAnalysis || !initialFullTest);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialAnalysis && initialFullTest) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const client = getApiClient();
    Promise.all([
      client.getScientificAnalysis(soilTestId),
      client.getSoilTest(soilTestId),
    ])
      .then(([a, t]) => {
        if (!cancelled) {
          setAnalysis(a);
          setFullTest(t);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [soilTestId, initialAnalysis, initialFullTest]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !analysis || !fullTest) {
    return (
      <Alert severity="error">
        Failed to load scientific charts: {error || "Incomplete data"}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} data-testid="scientific-charts-panel">
      <Typography variant="body2" sx={{ color: "workspace.textMuted", maxWidth: 720 }}>
        {textureTriangleOnly
          ? "Free plan includes the USDA texture triangle. Upgrade to Professional for the full scientific visualisation suite."
          : "Dark-native scientific visuals. Values come from the authoritative calculation engines; chart adapters only map them for display."}
      </Typography>
      <Grid container spacing={2}>
        <ChartCard
          title="USDA Texture Triangle"
          subheader="How sand, silt and clay classify this soil."
        >
          <TextureTriangleChart
            chartData={textureTriangleChartAdapter(analysis.texture)}
          />
        </ChartCard>

        {!textureTriangleOnly ? (
          <>
        <ChartCard
          title="Water Retention Curve"
          subheader="How much water the soil holds as it dries."
        >
          <WaterRetentionCurveChart
            chartData={waterRetentionChartAdapter(analysis.waterRetention)}
          />
        </ChartCard>

        <ChartCard
          title="Soil Water Summary"
          subheader="Saturation, field capacity, wilting point, plant-available water."
        >
          <SoilPhysicsWaterChart
            chartData={soilPhysicsChartAdapter(analysis.waterRetention)}
          />
        </ChartCard>

        <ChartCard
          title="Base Saturation"
          subheader="How calcium, magnesium, potassium and sodium share CEC."
        >
          <BaseSaturationChart
            chartData={baseSaturationChartAdapter(
              fullTest.chemistryResult || null
            )}
          />
        </ChartCard>

        <ChartCard
          title="Cation Balance"
          subheader="Calcium / magnesium / residual balance on the exchange complex."
        >
          <CationBalanceChart
            chartData={cationBalanceChartAdapter(analysis.structure)}
          />
        </ChartCard>

        <ChartCard
          title="Salinity & Sodicity"
          subheader="Salt and sodium risk signals for management."
        >
          <SalinitySodicityChart
            chartData={salinitySodicityChartAdapter(
              fullTest.interpretation || null,
              fullTest.chemistryResult || null,
              fullTest.physicsResult || null,
              fullTest.chemistryInput || null,
              fullTest.textureInput || null
            )}
          />
        </ChartCard>

        <ChartCard
          title="Evidence Completeness"
          subheader="How complete this test level is relative to its contract."
        >
          <EvidenceCompletenessChart
            chartData={evidenceCompletenessChartAdapter(analysis.coverage)}
          />
        </ChartCard>
          </>
        ) : null}
      </Grid>
    </Stack>
  );
}

function ChartCard({
  title,
  subheader,
  children,
}: {
  title: string;
  subheader: string;
  children: React.ReactNode;
}) {
  return (
    <Grid item xs={12} md={6}>
      <GlassSurface
        intensity="standard"
        depth="raised"
        sx={{ height: "100%", p: 0, overflow: "hidden" }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography component="h3" variant="h6">
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "workspace.textMuted" }}>
            {subheader}
          </Typography>
        </Box>
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "workspace.border",
            p: 2,
          }}
        >
          {children}
        </Box>
      </GlassSurface>
    </Grid>
  );
}
